import dayjs from 'dayjs'
import { utils, writeFileXLSX } from 'xlsx'
import { getMaxColumnWidths } from 'state/statements/statements.helpers'
import {
  downloadTransactionReportPdf,
  printTransactionReportPdf
} from 'pages/Transactions/components/TransactionReport/TransactionReport.pdf'
import {
  formatBillDate,
  getExcelHeading
} from 'pages/Transactions/components/TransactionReport/TransactionReport.helper'
import { ITransactionFilter, TRANSACTION_TYPE } from 'state/transaction/transaction.types'
import { ENTITY_TYPE } from 'shared/constants'
import { formatCurrency } from 'utils/formatCurrency'
import { IAccountReport } from './accountReport.builder'

export interface IAccountReportExportArgs {
  report: IAccountReport
  filter: ITransactionFilter
  business: { name: string; mobile: string; address: string }
  fileName?: string
}

const reportTitle = (entityType: ENTITY_TYPE): string =>
  entityType === ENTITY_TYPE.CUSTOMER ? 'All Customers' : 'All Suppliers'

/**
 * The consolidated PDF reuses the per-account generator with `showAccountColumn`, so
 * the account name gets its own column (separate from Notes) — matching the preview
 * and the individual report's layout.
 */
const toPdfArgs = ({ report, filter, business }: IAccountReportExportArgs) => ({
  entityType: report.entityType,
  entityName: reportTitle(report.entityType),
  currentBalance: report.combinedBalance,
  business,
  showAccountColumn: true,
  transactions: report.transactions.map((row) => ({
    ...row,
    accountName: row.accountDeleted ? `${row.accountName} (Deleted)` : row.accountName
  })),
  filter,
  transactionsReport: {
    // Discounts are reported separately (Android parity): excluded from payments and
    // surfaced on the PDF's "Discounts offered" line.
    paymentCount: report.paymentCount,
    creditCount: report.creditCount,
    payment: report.payment,
    credit: report.credit,
    discountCount: report.discountCount,
    discount: report.discount,
    transactions: [],
    loading: false,
    isReportUrlLoading: false,
    reportUrl: ''
  }
})

export const downloadAccountReportPdf = (args: IAccountReportExportArgs): void => {
  downloadTransactionReportPdf(toPdfArgs(args))
}

/**
 * Single-account statement PDF. Reuses the per-account generator with just this
 * account's rows — which already carry per-account running balances from the
 * builder — and that account's summary totals, so clicking one account in the
 * consolidated report opens the same statement as opening that account's own ledger.
 */
const toSingleAccountPdfArgs = (
  report: IAccountReport,
  accountId: string,
  filter: ITransactionFilter,
  business: IAccountReportExportArgs['business']
) => {
  const summary = report.summary.find((s) => s.id === accountId)
  const rows = report.transactions.filter((row) => row.accountId === accountId)
  const name = summary?.name || rows[0]?.accountName || ''
  return {
    entityType: report.entityType,
    entityName: summary?.deleted ? `${name} (Deleted)` : name,
    currentBalance: summary?.balance ?? 0,
    business,
    transactions: rows,
    filter,
    transactionsReport: {
      // Discounts reported separately (Android parity): see toPdfArgs.
      paymentCount: summary?.paymentCount ?? 0,
      creditCount: summary?.creditCount ?? 0,
      payment: summary?.payment ?? 0,
      credit: summary?.credit ?? 0,
      discountCount: summary?.discountCount ?? 0,
      discount: summary?.discount ?? 0,
      transactions: [],
      loading: false,
      isReportUrlLoading: false,
      reportUrl: ''
    }
  }
}

export const downloadSingleAccountPdf = (
  report: IAccountReport,
  accountId: string,
  filter: ITransactionFilter,
  business: IAccountReportExportArgs['business']
): void => {
  downloadTransactionReportPdf(toSingleAccountPdfArgs(report, accountId, filter, business))
}

export const printAccountReportPdf = (args: IAccountReportExportArgs): void => {
  printTransactionReportPdf(toPdfArgs(args))
}

/** Merged ledger export with an Account column, plus a totals header. */
export const downloadAccountReportExcel = ({
  report,
  filter,
  fileName
}: IAccountReportExportArgs): void => {
  const data = report.transactions.map((row) => ({
    Date: formatBillDate(Number(row.billedTime)),
    Account: row.accountDeleted ? `${row.accountName} (Deleted)` : row.accountName,
    Notes: row.note,
    Payment: row.transactionType === TRANSACTION_TYPE.PAYMENT ? row.amount : '',
    Credit: row.transactionType === TRANSACTION_TYPE.CREDIT ? row.amount : '',
    'Balance (₹)': `${Number(row.outStandingAmount || 0).toFixed(2)}`
  }))

  const title = reportTitle(report.entityType)
  const heading = getExcelHeading(filter as any, `${title} Report`)
  const balanceLabel = report.combinedBalance < 0 ? 'Net Due' : 'Net Advance'

  const worksheetData = [
    [heading],
    [],
    [`Downloaded On: ${dayjs().format('DD MMM, YYYY')}`],
    [`Accounts: ${report.accountsCount}`],
    [],
    [
      `Total Payments (${report.paymentCount})`,
      `Total Credits (${report.creditCount})`,
      `${balanceLabel}`
    ],
    [
      `₹ ${formatCurrency(report.payment)}`,
      `₹ ${formatCurrency(report.credit)}`,
      `₹ ${formatCurrency(Math.abs(report.combinedBalance))}`
    ],
    // Discounts are shown on their own line (Android parity), not folded into payments.
    ...(report.discount > 0
      ? [[`Discounts offered (${report.discountCount})`, `₹ ${formatCurrency(report.discount)}`]]
      : [])
  ]

  const wb = utils.book_new()
  const ws = utils.json_to_sheet(data, { origin: 'A9' })
  utils.sheet_add_aoa(ws, worksheetData, { origin: 'A1' })
  ws['!cols'] = getMaxColumnWidths(data)
  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, `${fileName || title} Report.xlsx`)
}
